import React from 'react';
import { Chip, Typography, Stack } from '@mui/joy';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';

const getFileIcon = (type) => {
  if (type === 'pdf') return <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'image'].includes(type)) {
    return <ImageOutlinedIcon sx={{ fontSize: 14 }} />;
  }
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 14 }} />;
};

const getFileType = (doc) => {
  if (doc.type) return doc.type;
  const name = doc.name || doc.path || '';
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  return ext || 'file';
};

const getFileName = (doc) => {
  if (doc.name) return doc.name;
  if (doc.path) {
    const parts = doc.path.split('/');
    return parts[parts.length - 1];
  }
  return 'Document';
};

const DocumentChip = ({
  document,
  onClick,
  onRemove,
  size = 'md',
  showOpenIcon = true,
  variant = 'soft',
  color = 'primary',
}) => {
  if (!document) return null;

  const fileName = getFileName(document);
  const fileType = getFileType(document);
  const displayName = fileName.length > 25 ? fileName.slice(0, 22) + '...' : fileName;

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove?.();
  };

  // Determine end decorator: close icon if onRemove, open icon if showOpenIcon, null otherwise
  const getEndDecorator = () => {
    if (onRemove) {
      return (
        <CloseIcon
          sx={{
            fontSize: 14,
            cursor: 'pointer',
            opacity: 0.7,
            pointerEvents: 'auto',
            '&:hover': { opacity: 1 },
          }}
          onClick={handleRemove}
        />
      );
    }
    if (showOpenIcon) {
      return <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.7 }} />;
    }
    return null;
  };

  return (
    <Chip
      variant={variant}
      color={color}
      size={size}
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        '&:hover': onClick ? {
          bgcolor: `${color}.softHoverBg`,
          transform: 'translateY(-1px)',
        } : {},
        '&:active': onClick ? {
          transform: 'translateY(0)',
        } : {},
      }}
      startDecorator={getFileIcon(fileType)}
      endDecorator={getEndDecorator()}
    >
      <Typography
        level="body-xs"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {displayName}
      </Typography>
    </Chip>
  );
};

// Variant for displaying in messages with quote information
export const DocumentQuoteChip = ({
  text,
  documentName,
  page,
  onRemove,
}) => {
  const displayText = text.length > 50 ? text.slice(0, 47) + '...' : text;
  const sourceInfo = page ? `${documentName}, p.${page}` : documentName;

  return (
    <Chip
      variant="outlined"
      color="neutral"
      size="sm"
      sx={{
        maxWidth: 300,
        height: 'auto',
        py: 0.5,
        '& .MuiChip-label': {
          display: 'block',
          whiteSpace: 'normal',
        },
      }}
      endDecorator={
        onRemove ? (
          <Typography
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            sx={{
              cursor: 'pointer',
              color: 'danger.500',
              '&:hover': { color: 'danger.600' },
              ml: 0.5,
            }}
          >
            x
          </Typography>
        ) : null
      }
    >
      <Stack spacing={0.25}>
        <Typography level="body-xs" sx={{ fontStyle: 'italic' }}>
          "{displayText}"
        </Typography>
        <Typography level="body-xs" color="neutral">
          from {sourceInfo}
        </Typography>
      </Stack>
    </Chip>
  );
};

// Variant for displaying image crop reference
export const ImageCropChip = ({
  documentName,
  page,
  thumbnailUrl,
  onRemove,
  onClick,
}) => {
  const sourceInfo = page ? `Region from ${documentName}, p.${page}` : `Region from ${documentName}`;

  return (
    <Chip
      variant="outlined"
      color="primary"
      size="sm"
      onClick={onClick}
      sx={{
        height: 'auto',
        py: 0.5,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { bgcolor: 'primary.softBg' } : {},
      }}
      startDecorator={
        thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt="Crop preview"
            style={{
              width: 24,
              height: 24,
              objectFit: 'cover',
              borderRadius: 2,
            }}
          />
        ) : (
          <ImageOutlinedIcon sx={{ fontSize: 16 }} />
        )
      }
      endDecorator={
        onRemove ? (
          <Typography
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            sx={{
              cursor: 'pointer',
              color: 'danger.500',
              '&:hover': { color: 'danger.600' },
              ml: 0.5,
            }}
          >
            x
          </Typography>
        ) : null
      }
    >
      <Typography level="body-xs">{sourceInfo}</Typography>
    </Chip>
  );
};

export default DocumentChip;
